import { observer } from 'mobx-react';
import React, { Component, MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import CancelDeliveryWarningModal from '../../../OrderRequestDelivery/components/CancelDeliveryWarningModal/CancelDeliveryWarningModal';
import { getOrderDeliveryTypeTitle } from 'src/constants/deliveryTypes';
import { ORDER_STATUS } from 'src/constants/order';
import { SCREENS } from 'src/constants/screens';
import { crmScreenRoles } from 'src/constants/user';
import { localeStore } from 'src/mobx/localesStore';
import { userStore } from 'src/mobx/userStore';
import { formatDueTime } from 'src/utils/formatter';
import { getTimeUntilEndDay } from 'src/utils/helpers';
import AggregatorManager from 'src/view/components/AggregatorManager/AggregatorManager';
import { historyPushWithSaveBackPath } from '~/helpers';
import { OrderStore } from '~/mobx/orderStore';
import { withOrderStore } from '~/view/screens/OrderScreen/context';
import getOrderLogPath, { LOGS } from '~/view/screens/OrderScreen/getOrderLogPath';
import './style.scss';

interface IItem {
  className?: string;
  label: string;
  value?: string | number;
  onClick?: () => void | undefined;
  children?: ReactNode;
}

const RenderItem: React.FC<IItem> = (item: IItem): JSX.Element => {
  return (
    <li className={`${item.className || ''}`} onClick={item.onClick}>
      <p className="family-bold text-size-10 color-dark text-uppercase letter-spacing-12">{localeStore.t(item.label)}</p>
      {item.children || <p className="family-semibold text-size-16 color-dark mt-1">{item.value}</p>}
    </li>
  );
};

interface IState {
  cancelDeliveryWarningModal: boolean;
}

// @ts-ignore
@withOrderStore
@observer
export default class OrderMainInfo extends Component<{ orderStore?: OrderStore }, IState> {
  state = {
    cancelDeliveryWarningModal: false,
  };
  timer: NodeJS.Timeout | null = null;

  get orderStore() {
    return this.props.orderStore!;
  }

  openCancelDeliveryWarningModal = () => {
    if (this.driverNotFound) this.setState({ cancelDeliveryWarningModal: true });
  };

  closeCancelDeliveryWarningModal = () => {
    this.setState({ cancelDeliveryWarningModal: false });
  };

  componentDidMount = () => {
    this.timer = setTimeout(this.forceRerender, getTimeUntilEndDay());
  };

  forceRerender = () => {
    this.timer = setTimeout(this.forceRerender, getTimeUntilEndDay());
    this.forceUpdate();
  };

  goToCustomer = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (this.pathToCustomerInCrmScreen) historyPushWithSaveBackPath(this.pathToCustomerInCrmScreen);
  };

  get customerInfo(): string {
    if (!this.orderStore.order) return '';

    if (this.orderStore.order.customer_mobile && this.orderStore.order.customer_name) {
      return this.orderStore.order.customer_mobile + ', ' + this.orderStore.order.customer_name;
    }

    return this.orderStore.order.customer_mobile || this.orderStore.order.customer_name || '';
  }

  get driverNotFound(): boolean {
    if (!this.orderStore.order || this.orderStore.order.dnf?.enable) return false;

    return this.orderStore.order.order_status_code === ORDER_STATUS.CANCELED && this.orderStore.order.takergo?.status === -3;
  }

  get logsPath(): string {
    if (!this.orderStore.order) return SCREENS.ROOT();
    return getOrderLogPath(this.orderStore.parentStore, LOGS.CONTENTS, this.orderStore.order?.id);
  }

  get pathToCustomerInCrmScreen(): string | null {
    if (!this.orderStore.order?.customer_id || !this.orderStore.order.customer_mobile) return null;
    if (crmScreenRoles.includes(userStore.user.role)) return SCREENS.CRM_PROFILE_EDIT(this.orderStore.order?.customer_id);
    return null;
  }

  render() {
    if (!this.orderStore.order) return null;

    return (
      <div className="flex-grow-1 pt-4 d-flex flex-column">
        <ul className="d-flex flex-row">
          <RenderItem className="pr-6" label="txt_total" value={this.orderStore.formatPrice(this.orderStore.order.total)} />
          {this.orderStore.order.edited ? (
            <RenderItem className="pr-6" label="order_edited">
              <div className="mt-1">
                <Link to={this.logsPath} className="color-mainblue text-size-16 family-semibold">
                  {localeStore.t('view_log')}
                </Link>
              </div>
            </RenderItem>
          ) : null}
          {/* order type */}
          <RenderItem className="pr-6" label="order_type" value={localeStore.t(getOrderDeliveryTypeTitle(this.orderStore.order))} />
          {/* dsp status */}
          <AggregatorManager aggregator={this.orderStore.order?.aggregator_key} sectionName="dsp_status">
            {this.orderStore.order.takergo?.dsp_status ? (
              <RenderItem
                className={`${this.driverNotFound ? 'order-main-info__dsp' : ''} mr-6`}
                label="dsp_status"
                value={this.orderStore.order.takergo?.dsp_status}
                onClick={this.openCancelDeliveryWarningModal}
              />
            ) : null}
          </AggregatorManager>
          {/* curbside status */}
          {this.orderStore.order.curbside ? (
            <RenderItem
              className="pr-6"
              label="curbside_status"
              value={localeStore.t(`${this.orderStore.order.curbside.client_arrived === 1 ? 'arrived' : 'not_arrived'}`)}
            />
          ) : null}
          {/* due time */}
          <AggregatorManager aggregator={this.orderStore.order?.aggregator_key} sectionName="due_time">
            <RenderItem className="pr-6" label="due_time" value={formatDueTime(this.orderStore.order.order_datetime)} />
          </AggregatorManager>
          {/* customer info */}
          <AggregatorManager aggregator={this.orderStore.order?.aggregator_key} sectionName="customer">
            <RenderItem label="client" className="pr-6" value={this.customerInfo}>
              {this.pathToCustomerInCrmScreen ? (
                <div className="mt-1">
                  <Link to={this.pathToCustomerInCrmScreen} onClick={this.goToCustomer} className="family-semibold color-mainblue text-size-16">
                    {this.customerInfo}
                  </Link>
                </div>
              ) : null}
            </RenderItem>
          </AggregatorManager>
          {this.orderStore.order?.rating?.avg_rating ? (
            <AggregatorManager aggregator={this.orderStore.order?.aggregator_key} sectionName="order_rating_details_page_hdr">
              <RenderItem className="pr-6" label="order_rating_details_page_hdr" value={this.orderStore.order?.rating?.avg_rating} />
            </AggregatorManager>
          ) : null}
        </ul>
        {/* cancelDeliveryWarningModal */}
        {this.driverNotFound ? (
          <CancelDeliveryWarningModal
            isOpen={this.state.cancelDeliveryWarningModal}
            closeCancelDeliveryWarningModal={this.closeCancelDeliveryWarningModal}
          />
        ) : null}
      </div>
    );
  }
}
